import type { LevelDef } from './types';

/**
 * Day 9 —Sunday, 2 a.m. The last walk. She took a taxi most of the way, but
 * he isn't behind her tonight: he is already standing outside her building.
 * She can sneak home past him, or go to the konbini and tell someone.
 */
export const night8: LevelDef = {
  id: 'night8',
  night: 9,
  title: 'Home',
  subtitle: 'Sunday · 02:04',
  look: 'casual',
  card: ['The taxi couldn\'t get down your street.', 'Two blocks to walk. Just two blocks.', 'Nobody is following you tonight.'],
  outfit: 'nightOut',
  clockStart: 2 * 60 + 4,
  map: [
    '#########################', // 0
    '############H############', // 1
    '############.############', // 2
    '############.############', // 3
    '######.......BL...#######', // 4
    '######.###.######.#######', // 5
    '######.###.######.#######', // 6
    '######.###.######.#######', // 7
    '##########.######T#######', // 8
    '##########L######.#######', // 9
    '##########.######.#######', // 10
    '##########.######.#######', // 11
    '##########.######.#######', // 12
    '##########.######.#######', // 13
    '####......V.......#######', // 14
    '####.############.#######', // 15
    '####.############.#######', // 16
    '####.############L#######', // 17
    '####.############.#######', // 18
    '####.############.#######', // 19
    '####F############.#######', // 20
    '####.############.#######', // 21
    '####.##########......####', // 22
    '####.##########.####.####', // 23
    '####.##########.####.####', // 24
    '####.##########.####.####', // 25
    '####.##########.####.LC##', // 26
    '####.##########.####.####', // 27
    '####......L.....####.####', // 28
    '####.###############.####', // 29
    '####.###############L####', // 30
    '####.###############.####', // 31
    '####.###############X####', // 32
    '####.###############.####', // 33
    '####....V....#######.####', // 34
    '####.#######.#######.####', // 35
    '####L#######.#######.####', // 36
    '####.#######.#######F####', // 37
    '####.#######.#######.####', // 38
    '####.#######.#######.####', // 39
    '####.#######.#######.####', // 40
    '####.#######.#######.####', // 41
    '####....L.......V....####', // 42
    '############.############', // 43
    '############.############', // 44
    '############.############', // 45
    '############L############', // 46
    '############.############', // 47
    '############.############', // 48
    '############S############', // 49
    '############.############', // 50
    '############P############', // 51
  ],
  zones: [
    { r0: 29, zone: 1 },
    { r0: 15, r1: 28, zone: 2 },
    { r1: 14, zone: 3 },
  ],
  battery: 20,
  pace: { tension: 0.55, patience: 700 },
  theme: {
    sky: '#06080e',
    fog: { color: '#0b0e15', density: 0.03 },
    hemi: { sky: '#46557a', ground: '#0c0c0d', intensity: 0.85 },
    ambient: 0.55,
    moon: 0.9,
  },
  stranger: { walkMax: 2.95, chaseSpeed: 3.15, gapMax: 30, spawnDelay: 0 },
  // Right outside her door.
  strangerStart: [12.5, 3.4],
  altGoal: { cell: [21, 26], ending: 'police', prompt: 'Going inside' },
  endings: {
    home: {
      title: 'She\'s home.',
      lines: [
        'She walked right past him. He stepped aside, like any stranger would.',
        '"Good night," he said. "See you on the train."',
        'The lock clicks. She doesn\'t sleep.',
      ],
      hint: 'Ending 1 of 2 — Home. There was another way: someone she could have told.',
    },
    police: {
      title: 'Someone believed her.',
      lines: [
        'The clerk locked the door and called 110 without asking a single question.',
        'They found him outside her building, still waiting.',
        'In his pocket: her keychain, and photos of her, taken from behind.',
        'He had ridden her train every night since spring.',
      ],
      hint: 'Ending 2 of 2 — The konbini. She told someone.',
    },
  },
  lines: {
    intro: 'Two blocks. No footsteps behind me. Nobody.',
    firstSight: 'Someone… outside my building. Standing under my window.',
    noticed: 'He\'s seen me. He\'s coming.',
    konbini: 'The konbini. The night clerk. Someone awake.',
    deadend: 'Dead end. Back, back.',
    roadworks: 'Road works. The quick way to the konbini is blocked.',
    homeStretch: 'My door. He\'s right there.',
  },
  mom: {
    first: { node: 'start', at: 10 },
    script: {
      start: {
        text: 'Are you in the taxi? I waited up.',
        options: [
          { text: 'Walking the last bit', next: 'window' },
          { text: 'Almost home', next: 'window' },
        ],
      },
      window: {
        text: "There's a man standing outside our building. Just standing there.",
        options: [
          { text: "It's him", next: 'konbini' },
          { text: "Maybe it's a neighbour", next: 'konbini' },
        ],
      },
      konbini: {
        text: "Don't come to the front door. Go to the konbini, I'm calling the police.",
        options: [
          { text: 'Ok', end: 'Stay with the clerk.' },
          { text: 'I just want to come home', end: 'Please. Go to the konbini.' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '20,26' || key === '20,25' || key === '20,27') ctx.sayOnce('konbini');
    if (key === '20,33' && !ctx.said('roadworks')) {
      ctx.spike(0.04);
      ctx.sayOnce('roadworks');
    }
    if (key === '6,7' && !ctx.said('deadend')) {
      ctx.spike(0.05);
      ctx.sayOnce('deadend');
    }
    if (cell.r === 4) ctx.sayOnce('homeStretch');
  },
};
